import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import RichTextEditor from "@/components/ui/RichTextEditor";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { axiosPrivate } from "@/api/axios";
import {
  StoredScheduledFact,
  buildScheduledFactKey,
  removeScheduledFactByKey,
  saveScheduledFact,
  saveScheduledFactWithKey,
} from "../utils/scheduledFactStorage";

const formSchema = z.object({
  date: z
    .string()
    .min(1, "Date is required")
    .regex(/^(\d{4}-\d{2}-\d{2})$/, "Invalid date format"),
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  image_url: z.string().url().optional(),
  reel_url: z.string().url().optional(),
});

export type FactSection = "gut-wellness" | "nutrition" | "sleep-exercise";

export interface AddFactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (payload: unknown) => void;
  section?: FactSection;
}

const sectionDisplay: Record<FactSection, string> = {
  "gut-wellness": "Gut & Wellness",
  nutrition: "Nutrition",
  "sleep-exercise": "Sleep & Exercise",
};

const sectionPlaceholders: Record<FactSection, { title: string; description: string }> = {
  "gut-wellness": {
    title: "Start your day with a glass of warm water and lemon",
    description:
      "A balanced gut microbiome strengthens immunity and supports digestion. Adding probiotics, fiber-rich foods, and staying hydrated can significantly improve your digestive health and overall wellness.",
  },
  nutrition: {
    title: "Include protein in every meal for sustained energy",
    description:
      "Balanced meals with lean protein, fiber, and healthy fats help sustain steady energy levels throughout the day. Focus on whole foods and avoid processed options for optimal nutrition.",
  },
  "sleep-exercise": {
    title: "Aim for 7-9 hours of quality sleep tonight",
    description:
      "Consistent sleep plus regular movement boosts mood, focus, and long-term resilience. Create a bedtime routine and incorporate daily physical activity for better health outcomes.",
  },
};

const sectionToApiType: Record<FactSection, string> = {
  "gut-wellness": "gut",
  nutrition: "nutrition",
  "sleep-exercise": "sleep",
};

export default function AddFactDialog({
  open,
  onOpenChange,
  onCreated,
  section = "gut-wellness",
}: AddFactDialogProps) {
  const [submitting, setSubmitting] = React.useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: new Date().toISOString().split("T")[0],
      title: "",
      description: "",
      image_url: undefined,
      reel_url: undefined,
    },
    mode: "onChange",
  });
  const mapSectionToType = React.useCallback(() => sectionToApiType[section] ?? "gut", [section]);

  const buildScheduledFactPayload = React.useCallback(
    (values: z.infer<typeof formSchema>): StoredScheduledFact => ({
      scheduled_date: values.date,
      type: mapSectionToType(),
      title: values.title,
    }),
    [mapSectionToType]
  );

  async function handleSubmit(values: z.infer<typeof formSchema>) {
    const apiType = mapSectionToType();

    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`;
    const isTodayDate = values.date === todayString;

    const payload = {
      title: values.title,
      description: values.description,
      type: apiType,
      is_tod: isTodayDate,
    };

    const tempKey = buildScheduledFactKey(`tmp_${Date.now()}`);
    const scheduledFactPayload = buildScheduledFactPayload(values);
    saveScheduledFactWithKey(tempKey, scheduledFactPayload);

    setSubmitting(true);
    try {
      const response = await axiosPrivate.post("/facts/", payload);
      if (response.data?.id) {
        saveScheduledFact(response.data.id, {
          ...scheduledFactPayload,
          id: Number(response.data.id),
        });
      }
      toast.success(isTodayDate ? "Tip of the day created successfully!" : "Tip scheduled successfully!");
      onCreated?.(response?.data ?? payload);
      form.reset();
      form.setValue("date", new Date().toISOString().split("T")[0]);
      onOpenChange(false);
    } catch (error: any) {
      const message = (error?.response?.data?.message as string) || "Failed to create tip";
      toast.error(message);
    } finally {
      removeScheduledFactByKey(tempKey);
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[95vw] sm:!w-full !max-w-[700px] sm:!max-w-[900px] sm:!min-h-[500px] !max-h-[95vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Create Fact of the Day</DialogTitle>
          <DialogDescription>
            Create a daily fact for {sectionDisplay[section] ?? sectionDisplay["gut-wellness"]} section.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                name="date"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Scheduled Date</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" min={new Date().toISOString().split("T")[0]} className="w-full" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              name="title"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Title</FormLabel>
                  <FormControl>
                    <Input placeholder={sectionPlaceholders[section]?.title ?? "Enter tip title"} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="description"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Description</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      value={field.value || ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={sectionPlaceholders[section]?.description ?? "Enter detailed description of the tip"}
                      minHeight="250px"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button className="w-full sm:w-auto" type="submit" disabled={!form.formState.isValid || submitting}>
                {submitting ? "Creating…" : "Create Tip"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
